import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./faq.css";

const faqs = [
  {
    question: "Who is eligible to become a Campus Ambassador?",
    answer: "Any currently enrolled college or university student with strong communication skills, an active social media presence, and a passion for Udghosh is welcome to apply."
  },
  {
    question: "How can I apply for the Campus Ambassador Program?",
    answer: "You can apply by registering through our official website. Just fill in your details and you're good to go!"
  },
  {
    question: "What responsibilities does a Campus Ambassador have?",
    answer: "As a Campus Ambassador, you'll promote Udghosh, organize campus events, gather feedback, create engaging content, and help grow our student community."
  },
  {
    question: "How long does the Campus Ambassador Program last?",
    answer: "The program usually runs for 6 months, with the option to extend based on your performance and interest."
  },
  {
    question: "What if I'm unable to fulfill my responsibilities?",
    answer: "We understand that academics come first. If you face any difficulties, reach out to our team — we'll try to make suitable arrangements or offer flexible alternatives."
  },
  {
    question: "How do I know if I've been selected?",
    answer: "Once you register on our portal, you can immediately start completing tasks and officially become part of the Udghosh Outreach Team."
  },
  {
    question: "What kind of events am I expected to organize?",
    answer: "You might organize a variety of events such as workshops, seminars, webinars, social campaigns, or creative campus promotions—tailored to your college environment."
  },
  {
    question: "What benefits do Campus Ambassadors receive?",
    answer: "You'll gain professional development, mentorship, certificates, exclusive perks like merchandise or gift cards, and the chance to network with students across India."
  },
  {
    question: "How does the point system work?",
    answer: "We use an 'Engagement Tokens' system that rewards you for activities like event management, social media outreach, and content creation. Your points determine your rankings and incentives."
  },
  {
    question: "How can I track my progress and points?",
    answer: "A personalized dashboard and leaderboard on our website keep you updated with your tasks, progress, and earned points in real-time."
  }
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const itemVariants = {
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="faq-section udg-section" id="faqs">
      {/* Header */}
      <div className="udg-section-header faq-header">
        <motion.span
          className="udg-eyebrow"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Got Questions?
        </motion.span>
        <motion.h2
          className="udg-section-title font-display"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          Frequently Asked Questions
        </motion.h2>
        <motion.span
          className="udg-gold-rule"
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          style={{ transformOrigin: "left" }}
        />
        <motion.p
          className="udg-lede"
          style={{ textAlign: "center" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          Find answers to the most commonly asked questions below.
        </motion.p>
      </div>

      {/* FAQ list */}
      <motion.div
        className="faq-list"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
      >
        {faqs.map((faq, i) => (
          <motion.div
            key={i}
            className={`faq-item udg-card${openIndex === i ? " faq-item--open" : ""}`}
            variants={itemVariants}
          >
            <button
              className="faq-question"
              onClick={() => toggle(i)}
              aria-expanded={openIndex === i}
            >
              <span className="faq-question-text">{faq.question}</span>
              <motion.span
                className="faq-chevron"
                animate={{ rotate: openIndex === i ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                aria-hidden="true"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.span>
            </button>

            <AnimatePresence initial={false}>
              {openIndex === i && (
                <motion.div
                  key="answer"
                  className="faq-answer"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.38, ease: "easeInOut" }}
                >
                  <p className="faq-answer-text">{faq.answer}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default FaqSection;
